/**
 * Attribution lead_id carry-through (WEB-1046).
 *
 * zoe-website mints an opaque `lead_id` at landing and keeps it on the URL
 * through the funnel. The Optimizely redirect into the quiz carries it here on
 * the entry URL; this module captures it IN MEMORY on boot and re-appends it
 * to the exit URL back to zoe.com, so the purchase attaches to the original
 * landing's attribution row instead of minting a fresh, campaign-less id.
 *
 * PECR constraint (same as zoe-website): the id must never be written to
 * cookie / localStorage / sessionStorage — memory and URLs only. A mid-quiz
 * hard reload loses it; zoe.com then mints a new id, which is the accepted
 * fallback.
 */
export const LEAD_ID_PARAM = 'lead_id'

// First-party destinations only — never leak the id to third-party hosts.
// localhost is included so the flow is verifiable in local development.
const FIRST_PARTY_HOST = /(^|\.)(zoe\.com|joinzoe\.com|zoe\.net|localhost)$/i

let storedLeadId: string | null = null

/** Captures the lead_id from the current URL into memory. Call once at boot. */
export function captureLeadIdFromUrl(): void {
  try {
    const value = new URLSearchParams(window.location.search).get(LEAD_ID_PARAM)?.trim()
    if (value) storedLeadId = value
  } catch {
    // never let attribution break the quiz
  }
}

/** The captured lead_id, or null when the user didn't arrive with one. */
export function getStoredLeadId(): string | null {
  return storedLeadId
}

/** Test-only: reset the in-memory store. */
export function resetStoredLeadId(): void {
  storedLeadId = null
}

/**
 * Returns `url` with the captured lead_id appended (preserving existing query
 * and hash). No-op when no id was captured or the destination is not a ZOE
 * first-party host.
 */
export function appendLeadId(url: string): string {
  if (!storedLeadId || !url) return url
  try {
    const isAbsolute = /^https?:\/\//i.test(url)
    const parsed = new URL(url, window.location.origin)
    if (!FIRST_PARTY_HOST.test(parsed.hostname)) return url
    parsed.searchParams.set(LEAD_ID_PARAM, storedLeadId)
    // Preserve the original form: relative in, relative out.
    return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}
